import * as React from 'react';
import { oc } from 'ts-optchain';
import clsx from 'clsx';

import { DisplayTime } from '@tock/time';
import { IconEdit, IconGrab, IconPin } from '@tocktix/pantry';
import { ServiceAerialTablePopoverPartyProps } from 'components/service/aerial/ServiceAerialTablePopoverPartyProps';
import { displayStatus, generateStateSelectorStatus, getButtonActions } from 'utils/partyUtils';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import IconButton from '@tock/shared/components/buttons/IconButton';
import PartyMoveButton from 'components/service/party/PartyMoveButton';
import PartyPanelStateSelector from 'components/service/party-panel/PartyPanelStateSelector';

// Legacy UI for the ServiceAerialTablePopover component. This was originally within the ServiceAerialTablePopover component,
// but was moved out to its own component to clean up the ServiceAerialTablePopover parent.
// This component should be deprecated and removed once all businesses are on the new refresh UI.
export const ServiceAerialTablePopoverPartyLegacy: React.FC<
  ServiceAerialTablePopoverPartyProps
> = ({
  dragMode,
  isFlexBusiness,
  isReadOnly,
  onAssignTables,
  onFinishParty,
  onOpenParty,
  onPrintChit,
  onSeatParty,
  onSuccessfulDrop,
  onTogglePin,
  onUpdatePartyState,
  party,
  partyPanelPosition,
  placement,
  printOnSeat,
  secondaryStates,
}) => {
  const {
    buttonText,
    buttonAction,
    buttonIcon,
    buttonColorClassname,
    buttonBorderClassname,
    isDisabled,
  } = getButtonActions(party, secondaryStates, onSeatParty, onFinishParty);
  const isCurrentParty = placement === 'current';

  return (
    <div className="ServiceAerialTable-popoverParty">
      <div
        className="PartyBodyCondensed-title ServiceAerialTable-popoverPartyName"
        onClick={onOpenParty}
      >
        <span className="PartyBodyCondensed-name">
          {party.lastNameFirstName}
          <div className="PartyBodyCondensed-edit">
            <IconEdit />
          </div>
        </span>
        <span className="PartyBodyCondensed-size">{party.partySize}</span>
      </div>
      {party.expectedReservationTime && (
        <div className="PartyBodyCondensed-description ServiceAerialTable-popoverDescription">
          <span className="PartyBodyCondensed-time">
            <DisplayTime timeValue={party.expectedReservationTime} />
          </span>
          <span className="PartyBodyCondensed-status">{displayStatus(party)}</span>
        </div>
      )}
      {isCurrentParty && (
        <PartyPanelStateSelector
          isDisabled={isReadOnly}
          party={party}
          status={generateStateSelectorStatus(party)}
          secondaryStates={secondaryStates}
          handleUpdatePartyState={onUpdatePartyState}
          position={partyPanelPosition}
          onPrintChit={onPrintChit}
          printOnSeat={printOnSeat}
        />
      )}

      <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--top">
        {!isCurrentParty && isFlexBusiness && party.type === 'Booking' && (
          <>
            <IconButton
              disabled={isReadOnly}
              className={oc(party).visit.pinnedToTable() ? 'ServiceAerialTable-pinned' : ''}
              IconComponent={IconPin}
              text={oc(party).visit.pinnedToTable() ? 'Unpin' : 'Pin'}
              hideText
              variant="secondary"
              onClick={onTogglePin}
            />
            <br />
          </>
        )}
        <PartyMoveButton
          IconComponent={IconGrab}
          dragMode={dragMode}
          isReadOnly={isReadOnly}
          stretch
          variant="primary"
          party={party}
          dragSource="TABLE_MENU"
          onClick={onAssignTables}
          onSuccessfulDrop={onSuccessfulDrop}
        />
      </ButtonGroup>

      {buttonText && (
        <ButtonGroup className="ServiceAerialTable-popoverButtons popoverButtons--mid">
          <IconButton
            IconComponent={buttonIcon}
            disabled={isReadOnly || isDisabled}
            text={buttonText}
            stretch
            modifierClassName={clsx(buttonColorClassname, buttonBorderClassname)}
            variant="primary"
            onClick={buttonAction}
          />
        </ButtonGroup>
      )}
    </div>
  );
};
